import { IconButton } from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { fetchDoorStatus, openDoor } from '../../action/depotsCommands';
import { Booking, Depot, DoorStatus, FullMappedDepot } from '../../api/types';
import ReloadIcon from '../../components/icons/ReloadIcon';
import SearchBar from '../../components/SearchBar';
import PageHeader from '../../components/PageHeader';
import PageHeadline from '../../components/PageHeadline';
import {
    assetListSelector,
    makeDoorStatusLoadingSelector,
    makeDoorStatusSelector,
} from '../../selector/depotsSelector';
import DoorList from './DoorList';
import OpenDoorDialog from './OpenDoorDialog';
import { doorApiSlice } from '../../api/door/doorApiSlice';
import LockOrUnlockDialog from './LockOrUnlockDialog';
import { enqueueErrorSnackbar, enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { useAuth0 } from '../../react-auth0-spa';

export interface FullMappedDoor {
    door: number;
    /** Null at a station with no lock controller: there is no lock to report. */
    doorState: 'open' | 'closed' | null;
    assetId: string;
    assetState: 'available' | 'blocked' | 'inUse' | 'soonAvailable';
    assetName: string;
    assetAccessories: string[];
}

interface DoorListWidgetProps {
    depot: Depot;
    doorNumber?: number;
    bookingId?: string;
    itemId?: string;
    booking?: Booking;
    children?: React.ReactNode;
}

const DoorListWidget = (props: DoorListWidgetProps) => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    // Recorded as actor.display on the door-open event (W2.1) — like
    // finishRental's actorEmail.
    const { user } = useAuth0();
    const [openDoorWarningOpen, setOpenDoorWarningOpen] =
        useState<boolean>(false);
    const [lockAssetWarningOpen, setLockAssetWarningOpen] =
        useState<boolean>(false);
    const [currentlySelectedDoor, setCurrentlySelectedDoor] =
        useState<number>(-1);
    // Pre-fill the locker search from a ?door= param so a deep link lands
    // directly on that door: "go to locker" on a booking detail, and "go to
    // locker" on a problem report, which is where an operator decides to take
    // the locker out of service (docs/plans/blocking-lockers-in-use.md).
    // The existing exact-number filter (DoorList.matchesSearch) does the rest,
    // so neither link needs a highlight mechanism of its own.
    const [searchParams] = useSearchParams();
    const [searchQuery, setSearchQuery] = useState<string>(
        searchParams.get('door') ?? '',
    );
    const doorStatus: DoorStatus | undefined = useSelector(
        makeDoorStatusSelector(props.depot.id),
    );
    // A reload fetches data that is already on screen, so it must not blank the
    // list out — the operator is usually still reading it. The indicator lives
    // on the button alone: the lockers below stay visible and stay the old
    // values until the fetch returns.
    const isReloadingDoorStatus = useSelector(
        makeDoorStatusLoadingSelector(props.depot.id),
    );
    const assetList = useSelector(assetListSelector);
    const [updateDoorState] = doorApiSlice.useUpdateDoorStateMutation();
    const [lockAction, setLockAction] = useState<'block' | 'unblock'>('block');

    let doorMapping: any = [];
    doorStatus?.doors.forEach((doorState) => {
        let mappedDoor: {
            assetId: string;
            door: number;
            assetName?: string;
            assetAccessories?: string[];
        } = { ...doorState };

        props.depot.doorMapping?.forEach((door) => {
            if (door.door === doorState.door) {
                assetList?.forEach((asset: any) => {
                    if (asset.id === door.assetId) {
                        mappedDoor = {
                            ...mappedDoor,
                            assetId: asset.id,
                            assetName: asset.name,
                            assetAccessories: asset.accessories,
                        };
                    }
                });
            }
        });
        doorMapping.push(mappedDoor);
    });

    if (props.doorNumber) {
        doorMapping = doorMapping.filter(
            (door: FullMappedDoor) => door.door === props.doorNumber,
        );
    }

    const fullMappedDepot: FullMappedDepot = {
        ...props.depot,
        doorMapping: doorMapping,
    };

    const showOpenDoorWarning = (doorNumber: number) => {
        setOpenDoorWarningOpen(true);
        setCurrentlySelectedDoor(doorNumber);
    };

    const lockOrUnlockDoor = async (action: 'block' | 'unblock', doorNumber: number) => {
        setCurrentlySelectedDoor(doorNumber);
        setLockAssetWarningOpen(true);
        setLockAction(action);
    };

    const handleOpenDoor = () => {
        dispatch(
            openDoor({
                depotId: props.depot.id,
                doorNumber: currentlySelectedDoor,
                actorEmail: user?.email,
            }),
        );
        setOpenDoorWarningOpen(false);
        setCurrentlySelectedDoor(-1);
    };

    const handleLockOrUnlockAsset = async () => {
        try {
            await updateDoorState({
                depotId: props.depot.id,
                door: currentlySelectedDoor,
                action: lockAction,
            }).unwrap();

            dispatch(enqueueSuccessSnackbar({
                message: t('depot_details.block_or_unblock_warning.success'),
            }));
            dispatch(fetchDoorStatus({ depotId: props.depot.id }));
            setLockAssetWarningOpen(false);
        } catch (error) {
            dispatch(enqueueErrorSnackbar({
                message: t('depot_details.block_or_unblock_warning.failure'),
            }));
            setLockAssetWarningOpen(false);
        }
    };

    // Not guarded on `isReloadingDoorStatus`: DoorList calls this through
    // `reloadDoorStatus` after a rebook, and skipping that because an earlier
    // fetch is still in flight would leave the rebooked locker showing its old
    // occupant. Stacked taps are prevented on the button itself instead.
    const handleReloadDoorStatus = () => {
        dispatch(fetchDoorStatus({ depotId: props.depot.id }));
    };

    return (
        <div>
            {!props.doorNumber && (
                <>
                    <PageHeader>
                        <div className={'flex-grow'}>
                            <PageHeadline>
                                {t('depot_details.locker_overview_title')}
                            </PageHeadline>
                            <p className={'text-sm text-foreground/60'}>
                                {doorMapping.length}{' '}
                                {t('common.lockers')}
                            </p>
                        </div>
                        <SearchBar
                            className={'hidden sm:block'}
                            placeholder={t('depot_details.search_placeholder')}
                            value={searchQuery}
                            onChange={setSearchQuery}
                        />
                        <IconButton
                            className={'text-foreground -mr-2'}
                            onClick={handleReloadDoorStatus}
                            disabled={isReloadingDoorStatus}
                            title={
                                isReloadingDoorStatus
                                    ? t('depot_details.reloading_door_status')
                                    : t('location_details.reload_door_status')
                            }
                            children={
                                <ReloadIcon
                                    className={
                                        isReloadingDoorStatus
                                            // Set on the svg itself, not inherited from the
                                            // button: MUI greys a disabled button's contents
                                            // out, which would leave the spinner barely
                                            // visible on a phone in daylight.
                                            ? 'animate-spin text-primary'
                                            : undefined
                                    }
                                />
                            }
                            size={'large'}
                        />
                    </PageHeader>
                    <SearchBar
                        className={'w-full sm:hidden mb-5'}
                        placeholder={t('depot_details.search_placeholder')}
                        value={searchQuery}
                        onChange={setSearchQuery}
                    />
                </>
            )}
            {props.children && (
                <div className={'mb-4'}>
                    {props.children}
                </div>
            )}
            <DoorList
                doorMapping={fullMappedDepot.doorMapping}
                depot={fullMappedDepot}
                searchQuery={searchQuery}
                onLockOrUnlockClick={lockOrUnlockDoor}
                onOpenClick={showOpenDoorWarning}
                reloadDoorStatus={handleReloadDoorStatus}
            />
            <OpenDoorDialog
                open={openDoorWarningOpen}
                doorNumber={currentlySelectedDoor}
                depotName={props.depot.name}
                onConfirm={handleOpenDoor}
                onCancel={() => setOpenDoorWarningOpen(false)}
            />
            <LockOrUnlockDialog
                action={lockAction}
                open={lockAssetWarningOpen}
                doorNumber={currentlySelectedDoor}
                depotName={props.depot.name}
                blocksRunningRental={
                    lockAction === 'block' &&
                    doorMapping.find(
                        (door: FullMappedDoor) => door.door === currentlySelectedDoor,
                    )?.assetState === 'inUse'
                }
                onConfirm={handleLockOrUnlockAsset}
                onCancel={() => setLockAssetWarningOpen(false)}
            />
        </div>
    );
};

export default DoorListWidget;
